/**
 * Converts raw KHR_interactivity extension data (glTF JSON) to our GraphData format.
 *
 * The two formats are structurally very similar — both based on the Khronos
 * interactivity spec. The main differences:
 *   - KHR socket references default `socket` to "value" (values) or "in" (flows)
 *   - KHR configuration entries lack a `type` field
 *   - KHR variables lack a `name` field
 *   - KHR value inline literals use `{type, value?}` with optional value
 *
 * Also handles TRACE_subgraph extensions:
 *   - Preserves `extensions.TRACE_subgraph` on declarations
 *   - Converts subgraph interface sockets on graph definitions
 *   - Returns all graphs (not just the active one) for multi-graph mode
 */

import type { GraphData, GraphNode, GraphVariable, GraphEvent, Declaration, TypeDefinition, ValueInput, FlowTarget, ConfigValue, ValueSocketDeclaration } from '../../scenedata/behaviors/behaviorgraphdata';
import type { KHRInteractivityData, KHRInteractivityGraph, KHRInteractivityNode, KHRInteractivityDeclaration } from '../../utils/ModelLoader';
import { TRACE_INTERACTIVITY_EXTENSION, traceOpContract, traceOpContractForWireOp } from './traceOpContracts';
import { rewriteNodeConfigForKHRImport, type ImportTranslationContext } from './nodeIndexTranslation';
import { signatureFromKHRType } from './khrTypeTable';

/**
 * Convert a KHR_interactivity extension payload to a GraphData.
 * Uses the first graph (or the one specified by `data.graph`).
 *
 * When `ctx` is provided, KHR `nodeIndex` / `pointer` configs on standard ops
 * are translated back to our TransformPath authoring form (relative to the
 * host where possible). Without `ctx`, configurations pass through verbatim.
 */
export function convertKHRInteractivityToGraphData(data: KHRInteractivityData, ctx?: ImportTranslationContext): GraphData {
    const graphIndex = data.graph ?? 0;
    const graph = data.graphs[graphIndex];
    if (!graph) {
        return { types: [], declarations: [], variables: [], events: [], nodes: [] };
    }
    return convertGraph(graph, ctx);
}

/**
 * Convert all graphs in a KHR_interactivity payload to GraphData[].
 * Used for multi-graph mode with TRACE_subgraph.
 */
export function convertKHRInteractivityToAllGraphs(data: KHRInteractivityData, ctx?: ImportTranslationContext): { graphs: GraphData[]; activeGraph: number } {
    return {
        graphs: data.graphs.map(g => convertGraph(g, ctx)),
        activeGraph: data.graph ?? 0,
    };
}

function convertGraph(graph: KHRInteractivityGraph, ctx?: ImportTranslationContext): GraphData {
    // 1:1 with the KHR table, so every type index in the document stays valid.
    const types: TypeDefinition[] = (graph.types ?? []).map(t => ({ signature: signatureFromKHRType(t) }));

    const declarations: Declaration[] = (graph.declarations ?? []).map(d => {
        // Socket declarations (`inputValueSockets` / `outputValueSockets`) are
        // wire-only: GraphData ops carry their sockets implicitly.
        const decl: Declaration = { op: graphOpForDeclaration(d) };
        if (d.extensions?.TRACE_subgraph) {
            decl.extensions = {
                TRACE_subgraph: { ...d.extensions.TRACE_subgraph },
            };
        }
        return decl;
    });

    const variables: GraphVariable[] = (graph.variables ?? []).map((v, i) => ({
        name: v.name ?? `var_${i}`,
        type: v.type,
        value: v.value ? [...v.value] : [],
    }));

    const events: GraphEvent[] = (graph.events ?? []).map(e => ({
        id: e.id ?? '',
        ...(e.values ? {
            values: Object.fromEntries(
                Object.entries(e.values).map(([key, val]) => [
                    key,
                    { type: val.type, value: val.value ? [...val.value] : [] },
                ])
            ),
        } : {}),
    }));

    const declOps = declarations.map(d => d.op);
    // Trace extension ops: the exporter sourced their unwired inputs with the
    // block default (see dropExportedDefaults).
    const traceExtensionDecls = (graph.declarations ?? []).map(
        d => d.extension === TRACE_INTERACTIVITY_EXTENSION || !!d.extensions?.TRACE_subgraph,
    );
    const nodes: GraphNode[] = (graph.nodes ?? []).map(n => convertNode(n, declOps, traceExtensionDecls, ctx));

    const result: GraphData = { types, declarations, variables, events, nodes };

    // Convert subgraph interface sockets
    if (graph.inputFlowSockets) {
        result.inputFlowSockets = Object.fromEntries(
            Object.entries(graph.inputFlowSockets).map(([k, v]) => [k, v])
        );
    }
    if (graph.outputFlowSockets) {
        result.outputFlowSockets = Object.fromEntries(
            Object.entries(graph.outputFlowSockets).map(([k, v]) => [k, v])
        );
    }
    if (graph.inputValueSockets) {
        result.inputValueSockets = Object.fromEntries(
            Object.entries(graph.inputValueSockets).map(([k, v]) => [k, v as ValueSocketDeclaration])
        );
    }
    if (graph.outputValueSockets) {
        result.outputValueSockets = Object.fromEntries(
            Object.entries(graph.outputValueSockets).map(([k, v]) => [k, v])
        );
    }

    return result;
}

/**
 * The GraphData op for a KHR declaration: TRACE_interactivity wire names map
 * back through the op contracts (`setActive` → `trace/setActive`, `pagesNext`
 * → `pages/next`, `pointerSet` → `pointer/set`); a subgraph invocation keeps
 * its name whatever its `extension`; everything else — core ops and the
 * select/hover extension events — is already the GraphData op.
 */
function graphOpForDeclaration(d: KHRInteractivityDeclaration): string {
    if (d.extensions?.TRACE_subgraph) return d.op;
    if (d.extension === TRACE_INTERACTIVITY_EXTENSION) {
        return traceOpContractForWireOp(d.op)?.graphOp ?? `trace/${d.op}`;
    }
    return d.op;
}

const POINTER_OPS = new Set(['pointer/get', 'pointer/set', 'pointer/interpolate']);

function convertNode(
    node: KHRInteractivityNode,
    declOps: readonly string[],
    traceExtensionDecls: readonly boolean[],
    ctx?: ImportTranslationContext,
): GraphNode {
    const result: GraphNode = {
        declaration: node.declaration,
    };

    if (node.configuration) {
        const configuration: Record<string, ConfigValue> = {};
        for (const [key, cfg] of Object.entries(node.configuration)) {
            configuration[key] = {
                type: 0,
                value: cfg.value ? [...cfg.value] : [],
            };
        }
        result.configuration = ctx
            ? (rewriteNodeConfigForKHRImport({ ...result, configuration }, declOps, ctx) ?? configuration)
            : configuration;
    }

    if (node.values) {
        const values: Record<string, ValueInput> = {};
        for (const [key, val] of Object.entries(node.values)) {
            if ('node' in val) {
                values[key] = {
                    node: val.node,
                    socket: val.socket ?? 'value',
                };
            } else {
                values[key] = {
                    type: val.type,
                    value: val.value ? [...val.value] : [],
                };
            }
        }
        result.values = values;
    }

    if (node.flows) {
        const flows: Record<string, FlowTarget> = {};
        for (const [key, flow] of Object.entries(node.flows)) {
            flows[key] = {
                node: flow.node,
                socket: flow.socket ?? 'in',
            };
        }
        result.flows = flows;
    }

    // glTF `extras` carries our authoring metadata (editor x/y, macro tags)
    // across the KHR boundary — read it back so `.trace` saves and macro
    // recognition keep working after a KHR import.
    if (node.extras && Object.keys(node.extras).length > 0) {
        result.metadata = { ...node.extras };
    }

    const op = declOps[node.declaration];
    if (op && traceExtensionDecls[node.declaration]) dropExportedDefaults(result, op);
    if (op === 'variable/set') fromRatifiedVariableSet(result);
    if (op && POINTER_OPS.has(op)) normalizeImportedPointer(result);

    return result;
}

/**
 * The spec wants every declared input of an extension op sourced, so the
 * exporter writes an unwired input as the block's own default (or an empty
 * literal, the type default). Such a literal means exactly what leaving the
 * input unwired means, so it comes back unwired and the editor shows no
 * override.
 */
function dropExportedDefaults(node: GraphNode, op: string): void {
    if (!node.values) return;
    const defaults = traceOpContract(op)?.defaults ?? {};
    for (const [name, input] of Object.entries(node.values)) {
        if ('node' in input) continue;
        const value = input.value ?? [];
        const fallback = defaults[name];
        if (value.length === 0 || (fallback && JSON.stringify(value) === JSON.stringify(fallback))) {
            delete node.values[name];
        }
    }
    if (Object.keys(node.values).length === 0) delete node.values;
}

/**
 * Ratified `variable/set` (`variables: [i]`, input socket `"i"`) → our
 * single-variable authoring form (`variable: [i]`, input `value`). Multi-
 * variable sets have no GraphData equivalent and are left as-is.
 */
function fromRatifiedVariableSet(node: GraphNode): void {
    const variables = node.configuration?.variables?.value;
    if (!variables || variables.length !== 1 || node.configuration?.variable) return;
    const index = variables[0];
    const { variables: _variables, ...rest } = node.configuration!;
    node.configuration = { ...rest, variable: { type: 0, value: [index] } };
    const key = String(index);
    if (node.values?.[key]) {
        const { [key]: value, ...others } = node.values;
        node.values = { ...others, value };
    }
}

/**
 * Pointer ops: the `type` configuration is derivable once the pointer became
 * target + property, so it is dropped there; a pointer left in wire form keeps
 * it. A pre-ratification `{x}` template segment fed an integer is read as the
 * ratified `[x]`.
 */
function normalizeImportedPointer(node: GraphNode): void {
    const configuration = node.configuration;
    if (!configuration) return;
    if (configuration.target && configuration.property && !configuration.pointer) {
        const { type: _type, ...rest } = configuration;
        node.configuration = rest;
        return;
    }
    const pointer = configuration.pointer?.value?.[0];
    if (typeof pointer !== 'string') return;
    const ratified = pointer.replace(/\{([^{}/]+)\}/g, (whole, name: string) => {
        const v = node.values?.[name];
        const component = v && !('node' in v) ? v.value?.[0] : undefined;
        return typeof component === 'number' && Number.isInteger(component) ? `[${name}]` : whole;
    });
    if (ratified !== pointer) configuration.pointer = { ...configuration.pointer, value: [ratified] };
}
